// The map's hold on the one clock: whether it may run while the Atlas has
// the camera, and why not when it cannot. src/main.ts and src/explore.ts
// each read a coasting ship's own fields off the cruise handle into this
// every frame the map is open; only the fight branch differs between them,
// so `readMapHold`'s caller passes `fightOn` rather than this module
// guessing at it.

import { gameClock } from '../world/clock';
import type { CruiseDrive } from './drive';
import type { FlightPull, FlightState } from '../sim/flight';
import type { FlightGround } from '../sim/ground';
import type { GravityField } from '../sim/gravityField';
import type { LandVerbHandle } from '../sim/landVerb';
import type { BodyRow } from '../world/types';
import type { WorldVec } from '../world/frame';
import {
  earliestImpact, MAP_HOLD_IMPACT_S, mapClockHold, type ImpactSlot, type MapHold,
} from '../sim/mapClockHold';

export interface MapHoldDeps {
  readonly landVerb: LandVerbHandle;
  readonly flight: FlightState;
  readonly gravityField: GravityField;
  readonly cruiseDrive: CruiseDrive;
  readonly _pulls: readonly FlightPull[];
  readonly _ground: FlightGround;
  readonly _pullRows: readonly (BodyRow | null)[];
  readonly _pullIds: readonly string[];
  readonly shipWorldAt: () => WorldVec;
}

export interface MapHoldHandle {
  /** Why the map holds the clock this frame, or null while it may run - the
   *  reading `applyMapHold` last set. */
  readonly current: MapHold | null;
  /**
   * Folds a coasting ship's ground read, the autopilot's next burn and
   * (main.ts only) a mounted fight into one hold reading, or null where the
   * map may run. The caller applies it with `applyMapHold`.
   */
  readMapHold(realDt: number, fightOn: boolean): MapHold | null;
  /** Hold or release the one clock for the map. Only a pause this set is
   *  undone here. */
  applyMapHold(next: MapHold | null): void;
}

export function createMapHold(deps: MapHoldDeps): MapHoldHandle {
  const {
    landVerb, flight, gravityField, cruiseDrive, _pulls, _ground, _pullRows, _pullIds, shipWorldAt,
  } = deps;
  let mapHold: MapHold | null = null;

  /** Seconds to the first ground a coasting ship meets, and whose, or null
   *  where it is not coasting: landed, parked by the land verb, or pulled by
   *  nothing. */
  function coastImpact(horizonS: number): { s: number; body: string } | null {
    if (landVerb.open || flight.grounded !== null) return null;
    const idx = gravityField.frameParentIdx;
    const pull = idx < 0 ? undefined : _pulls[idx];
    if (!pull || !(pull.gm > 0)) return null;
    // The bound the flight's own ground contact meets first: the datum on a sphere
    // (drawn relief there is not a contact surface), the shape's outer bound on a
    // shape-model body such as Phobos.
    const boundM = (i: number): number => {
      const datumM = _ground.radiiM[i] ?? 0;
      return datumM > 0 ? Math.max(datumM, _ground.outerRadiiM?.[i] ?? 0) : 0;
    };
    const nameOf = (i: number): string => _pullRows[i]?.display_name ?? _pullIds[i] ?? '';
    const at = shipWorldAt();
    const others: ImpactSlot[] = [];
    const n = Math.min(_pulls.length, _ground.radiiM.length);
    for (let i = 0; i < n; i++) {
      const p = _pulls[i]!;
      if (i === idx || !(p.gm > 0) || !(boundM(i) > 0)) continue;
      others.push({
        name: nameOf(i),
        gm: p.gm,
        radiusM: boundM(i),
        rel: { x: p.at.x - pull.at.x, y: p.at.y - pull.at.y, z: p.at.z - pull.at.z },
        vel: _ground.velIn?.[i] ?? { x: 0, y: 0, z: 0 },
      });
    }
    const hit = earliestImpact(
      { rel: { x: at.x - pull.at.x, y: at.y - pull.at.y, z: at.z - pull.at.z }, vel: flight.velocity },
      { name: nameOf(idx), gm: pull.gm, radiusM: boundM(idx) },
      others, horizonS);
    return { s: hit.s, body: hit.name };
  }

  function readMapHold(realDt: number, fightOn: boolean): MapHold | null {
    const gc = gameClock();
    const stepS = realDt * Math.max(gc.rate, gc.warpTarget);
    const dueT = cruiseDrive.autopilotDueT;
    const impact = fightOn ? null : coastImpact(MAP_HOLD_IMPACT_S + stepS);
    return mapClockHold({
      fightOn,
      burnDueInS: dueT === null ? null : dueT - gc.t,
      impactInS: impact?.s ?? null,
      impactBody: impact?.body ?? '',
      stepS,
    });
  }

  function applyMapHold(next: MapHold | null): void {
    if (next !== null) gameClock().pause();
    else if (mapHold !== null) gameClock().resume();
    mapHold = next;
  }

  return {
    get current() { return mapHold; },
    readMapHold,
    applyMapHold,
  };
}
